import React, { useState, useRef, useEffect } from 'react';
import API from '../api';
import { useAuth } from '../context/AuthContext';

const ChatBot = () => {
  const { user } = useAuth(); // We still use this to get the name/role if needed
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: '👋 Hi! I am Lucy. I can check your stock, sales, and expiring medicines. How can I help?' 
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  // ✅ UPDATED HELPER FUNCTION TO READ YOUR SPECIFIC STORAGE
  const getToken = () => {
    // 1. DIRECT KEY CHECK (Based on your provided data)
    const directToken = localStorage.getItem('token');
    if (directToken) return directToken;

    // 2. Fallback: Check inside 'user' object if your app changes later
    const userObj = localStorage.getItem('user');
    if (userObj) {
        try {
            const parsed = JSON.parse(userObj);
            if (parsed.token) return parsed.token;
        } catch(e) {}
    }

    return null;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Get the token using the new logic
    const token = getToken();

    // Debugging: This will show up in your browser console (F12)
    console.log("🔍 Token Found:", token ? "YES" : "NO");

    if (!token) {
      setMessages(prev => [...prev, { role: 'assistant', content: "⚠️ Please log in to access your pharmacy data." }]);
      return;
    }

    const userMessage = input.trim();
    setInput('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // ✅ Sends the token found in 'token' key
        },
      };

      // Send to backend
      const response = await API.post('/chat', { message: userMessage }, config);

      if (response.data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.reply }]);
      }
    } catch (error) {
      console.error("Chat Error:", error);
      let errorMsg = "Sorry, I'm having trouble accessing the server.";
      
      if (error.response?.status === 401) {
        errorMsg = "⚠️ Session expired. Please log in again.";
      } else if (error.response?.status === 429) {
        errorMsg = "⏳ I'm a bit busy. Please try again in a few seconds.";
      }

      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#319795', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          zIndex: 9999,
          transition: 'transform 0.3s ease',
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
        }}
      >
        <span style={{ fontSize: '28px', color: 'white' }}>
          {isOpen ? '✕' : '🤖'}
        </span>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: '350px',
          height: '500px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          boxShadow: '0 5px 25px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          
          {/* Header */}
          <div style={{
            padding: '15px',
            backgroundColor: '#319795',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderBottom: '1px solid #2c7a7b'
          }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: '5px' }}>
              🤖
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Lucy</div>
              <div style={{ fontSize: '11px', opacity: 0.9 }}>
                Online • {localStorage.getItem('pharmacyName') || 'Your Pharmacy'}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div style={{
            flex: 1,
            padding: '15px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: '#f7fafc'
          }}>
            {messages.map((msg, index) => (
              <div 
                key={index} 
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  backgroundColor: msg.role === 'user' ? '#319795' : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#2d3748',
                  padding: '12px 16px',
                  borderRadius: msg.role === 'user' ? '15px 15px 0 15px' : '15px 15px 15px 0',
                  maxWidth: '85%',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  border: msg.role === 'user' ? 'none' : '1px solid #e2e8f0'
                }}
              >
                {/* Handle newlines properly */}
                {msg.content.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i !== msg.content.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            ))}
            
            {loading && (
              <div style={{ alignSelf: 'flex-start', marginLeft: '10px', marginBottom: '10px' }}>
                <div style={{ 
                  backgroundColor: '#edf2f7', 
                  padding: '8px 15px', 
                  borderRadius: '15px', 
                  fontSize: '12px', 
                  color: '#718096',
                  fontStyle: 'italic' 
                }}>
                  Thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSend} style={{
            padding: '15px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '10px'
          }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about stock, sales..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#f7fafc',
                color: '#2d3748',
                outline: 'none',
                fontSize: '14px'
              }}
            />
            <button 
              type="submit" 
              disabled={loading || !input.trim()}
              style={{
                background: loading ? '#cbd5e0' : '#319795',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                width: '45px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;